import React, { useEffect, useMemo, useRef, useState } from "react";
import ForceGraph2D from "react-force-graph-2d";
import * as d3 from "d3-force-3d";

import data from "./data/data_date.json";
import titlesData from "./data/titles_date.json";
import HomeButton from "./utils/HomeButton";
import { FloatingPanelStack } from "./utils/FloatingPanelStack";
import { DetailsPanel } from "./utils/DetailsPanel";
import { FloatingPanel } from "./utils/FloatingPanel";
import { drawRoundedRect, getConnectedComponents } from "./graph/common";

function parseDateSafe(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  if (d.getFullYear() < 2016) return null; // filter erroneous
  return d;
}

function buildLookupMaps(data) {
  const claimMap = new Map(data.claims.map(c => [c.id, c]));
  const eventMap = new Map(data.events.map(e => [e.id, e]));
  return { claimMap, eventMap };
}

function computeClusterAvgDate(members, claimMap, eventMap) {
  const dates = [];

  members.forEach(id => {
    const c = claimMap.get(id);
    const e = eventMap.get(id);

    const raw = c?.date || e?.date;
    const parsed = parseDateSafe(raw);

    if (parsed) dates.push(parsed.getTime());
  });

  if (!dates.length) return null;

  const avg = dates.reduce((a, b) => a + b, 0) / dates.length;
  return new Date(avg);
}

function buildGraph(data) {
  const nodes = [];
  const links = [];

  const titleMap = new Map(titlesData.map(t => [t.cluster_id, t.title]));
  const { claimMap, eventMap } = buildLookupMaps(data);

  data.claim_clusters.forEach((cluster) => {
    const avgDate = computeClusterAvgDate(cluster.members, claimMap, eventMap);

    nodes.push({
      id: cluster.cluster_id,
      label: titleMap.get(cluster.cluster_id) || cluster.title || "Unnamed Claim Cluster",
      type: "claim_cluster",
      type_nice: "Claim",
      members: cluster.members,
      avgDate
    });
  });

  data.event_clusters.forEach((cluster) => {
    const avgDate = computeClusterAvgDate(cluster.members, claimMap, eventMap);

    nodes.push({
      id: cluster.cluster_id,
      label: titleMap.get(cluster.cluster_id) || cluster.title || "Unnamed Event Cluster",
      type: "event_cluster",
      type_nice: "Event",
      members: cluster.members,
      avgDate
    });
  });

  data.cluster_links.forEach((link) => {
    links.push({
      source: link.claim_cluster_id,
      target: link.event_cluster_id
    });
  });

  return { nodes, links };
}


export function VizTimeFilter() {
  const fgRef = useRef();
  const detailsPanelRef = useRef();
  const [selectedNode, setSelectedNode] = useState(null);
  const [inputDate, setInputDate] = useState(1682353753000); //some time in 2023
  const [showAll, setShowAll] = useState(false);
  const [filterLeeway, setFilterLeeway] = useState(6);

  const parsedInputDate = useMemo(() => {
    const d = new Date(inputDate);
    return isNaN(d.getTime()) ? null : d;
  }, [inputDate]);

  const graphData = useMemo(() => {
    const full = buildGraph(data);
    const components = getConnectedComponents(full.nodes, full.links);

    const validIds = new Set(
      components.filter(c => c.length > 1000).flat()
    );

    return {
      nodes: full.nodes.filter(n => validIds.has(n.id)),
      links: full.links.filter(
        l => validIds.has(l.source) && validIds.has(l.target)
      )
    };
  }, []);

  useEffect(() => {
    if (!fgRef.current) return;

    fgRef.current.d3Force("charge", d3.forceManyBody().strength(-30));

    fgRef.current.d3Force(
      "link",
      d3.forceLink().distance(140)
    );

    fgRef.current.d3Force(
      "collision",
      d3.forceCollide((node) => {
        const dims = node.__bckgDimensions;
        return dims ? Math.max(dims[0], dims[1]) / 2 + 32 : 40;
      })
    );

    fgRef.current.d3ReheatSimulation();
  }, [graphData]);

  const timeRange = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;

    [...data.claims, ...data.events].forEach(d => {
      if (!d.date) return;
      const t = parseDateSafe(d.date)?.getTime();
      if (t == null) {
        return
      }
      if (t < min) min = t;
      if (t > max) max = t;
    });

    return { min, max };
  }, []);


  function isNodeHighlighted(node, referenceDate) {
    if (!referenceDate || !node.avgDate) return false;
    const diffMonths = Math.abs(referenceDate - node.avgDate) / (1000 * 60 * 60 * 24 * 30.44);
    return diffMonths <= filterLeeway;
  }

  const highlightedNodeIds = useMemo(() => {
    if (!parsedInputDate) return new Set();

    const set = new Set();

    graphData.nodes.forEach((n) => {
      if (showAll || isNodeHighlighted(n, parsedInputDate)) {
        set.add(n.id);
      }
    });

    return set;
  }, [graphData.nodes, parsedInputDate, showAll, filterLeeway]);

  useEffect(() => {
    if (fgRef.current) {
      fgRef.current.zoom(0.01, 0);
    }
  }, []);

  return (
    <div>
      <HomeButton />
      <ForceGraph2D
        ref={fgRef}
        graphData={graphData}
        nodeLabel={(node) => node.label}
        nodeAutoColorBy="type"
        linkDirectionalArrowLength={500}
        linkDirectionalArrowRelPos={0.9}
        linkColor={(link) => {
          const sourceId =
            typeof link.source === "object" ? link.source.id : link.source;

          const targetId =
            typeof link.target === "object" ? link.target.id : link.target;

          const bothHighlighted =
            highlightedNodeIds.has(sourceId) &&
            highlightedNodeIds.has(targetId);

          return bothHighlighted ? "black" : "rgba(0,0,0,0)";
        }}
        linkWidth={2.5}
        onNodeClick={(node) => { detailsPanelRef.current.open(); setSelectedNode(node)}}
        nodeCanvasObject={(node, ctx) => {
          const label = node.label;

          const fontSize = 32 + 64 * Math.min(node.members.length, 5);
          ctx.font = `${fontSize}px Sans-Serif`;

          const textWidth = ctx.measureText(label).width;
          const padding = fontSize * 0.6;

          const width = textWidth + padding;
          const height = fontSize + padding;

          const x = node.x - width / 2;
          const y = node.y - height / 2;

          const radius = node.type.includes("claim") ? fontSize * 6 : 0;

          ctx.fillStyle = node.type.includes("claim")
            ? "DarkMagenta"
            : "green"

          if (highlightedNodeIds.has(node.id)) {
            drawRoundedRect(ctx, x, y, width, height, radius);
            ctx.fill();
            ctx.strokeStyle = "white";
            ctx.stroke();

            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillStyle = "white";
            ctx.fillText(label, node.x, node.y);

          }



          node.__bckgDimensions = [width, height];
          node.__bckgPos = { x, y };
        }}
        nodePointerAreaPaint={(node, color, ctx) => {
          if (!highlightedNodeIds.has(node.id)) return;

          const dims = node.__bckgDimensions;
          const pos = node.__bckgPos;

          if (!dims || !pos) return;

          ctx.fillStyle = color;
          drawRoundedRect(ctx, pos.x, pos.y, dims[0], dims[1], 6);
          ctx.fill();
        }}
      />
      <FloatingPanelStack bot="lg">
        <DetailsPanel selectedNode={selectedNode} data={data} ref={detailsPanelRef} />
        <FloatingPanel title={"Key"}>
          <p style={{ backgroundColor: "green" }} className="text-white p-1 text-xl mb-3">Trigger Event Cluster</p>
          <p style={{ backgroundColor: "DarkMagenta" }} className="text-white p-1 text-xl rounded-3xl">Claim Cluster</p>
        </FloatingPanel>
        <FloatingPanel title={"Config"} defaultOpen={false}>

          <label className="flex">
            <span className="mr-1 grow">Show all</span>
            <input
              type="checkbox"
              checked={showAll}
              onChange={() => setShowAll(!showAll)}
            />
          </label>
          <br />
          <label className="flex">
            <span className="mr-1 grow">Date Window (mos)</span>
            <input
              className="w-16 border-2"
              type="number"
              value={filterLeeway}
              onChange={(e) => setFilterLeeway(Number(e.target.value))}
            />
          </label>
        </FloatingPanel>
      </FloatingPanelStack>

      <div
        className="fixed bottom-0 left-0 w-full bg-gray-600 p-3 z-10"
      >
        <input
          type="range"
          min={timeRange.min}
          max={timeRange.max}
          value={inputDate}
          onChange={(e) => setInputDate(new Number(e.target.value))}
          className="w-full"
        />


        <span className="text-white">
          {new Date(inputDate).toISOString().slice(0, 10)} (± {filterLeeway} month(s) window)
        </span>

      </div>
    </div>
  );
}